// src/components/PanneauJourCreateur.jsx
// 🆕 NOUVEAU FICHIER
//
// Événements d'un jour, avec accès à l'édition
// et création pré-remplie.

import React from "react";
import { useNavigate } from "react-router-dom";
import {
    CalendarPlus,
    Eye,
    Heart,
    MapPin,
    MousePointerClick,
    Pencil,
} from "lucide-react";

import { heureDe } from "../services/AgendaCreateurService";

const MOIS = [
    "janvier", "février", "mars", "avril", "mai", "juin",
    "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

const JOURS = [
    "Dimanche", "Lundi", "Mardi", "Mercredi",
    "Jeudi", "Vendredi", "Samedi",
];

const PanneauJourCreateur = ({ date, evenements = [] }) => {
    const navigate = useNavigate();

    if (!date) {
        return (
            <div className="rounded-2xl border border-white/10
                            bg-white/[0.02] p-8 text-center">
                <CalendarPlus
                    size={36}
                    className="mx-auto text-white/20 mb-3"
                    aria-hidden="true"
                />
                <p className="text-gray-400 text-sm">
                    Sélectionnez une date pour voir ou ajouter
                    des événements
                </p>
            </div>
        );
    }

    const titre = `${JOURS[date.getDay()]} ${date.getDate()} ${MOIS[date.getMonth()]}`;

    const estPasse = (() => {
        const auj = new Date();
        auj.setHours(0, 0, 0, 0);
        return date < auj;
    })();

    /** Ouvre le formulaire avec la date pré-remplie */
    const ajouterCeJour = () => {
        const iso = [
            date.getFullYear(),
            String(date.getMonth() + 1).padStart(2, "0"),
            String(date.getDate()).padStart(2, "0"),
        ].join("-");

        navigate(`/EspaceClient/mesevents/ajouter?date=${iso}`);
    };

    return (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02]
                        overflow-hidden">

            {/* ══ EN-TÊTE ══ */}
            <div className="px-5 pt-4 pb-3 border-b border-white/10">
                <h2 className="text-white text-base font-bold capitalize">
                    {titre}
                </h2>
                <p className="text-gray-500 text-sm mt-0.5">
                    {evenements.length === 0
                        ? "Aucun événement"
                        : `${evenements.length} événement${evenements.length > 1 ? "s" : ""}`}
                </p>
            </div>

            {/* ══ LISTE ══ */}
            {evenements.length > 0 && (
                <ul className="p-3 space-y-2 list-none m-0 max-h-[420px]
                               overflow-y-auto">
                    {evenements.map((e) => (
                        <li key={e.id}>
                            <button
                                type="button"
                                onClick={() =>
                                    navigate(`/EspaceClient/mesevents/update/${e.id}`)
                                }
                                className="w-full text-left flex gap-3 p-2.5
                                           rounded-xl hover:bg-white/5 transition
                                           focus:outline-none focus:ring-2
                                           focus:ring-[#5F33BA]"
                            >
                                {/* Image */}
                                <div className="w-14 h-14 shrink-0 rounded-lg
                                                overflow-hidden bg-[#2D1B69]
                                                relative">
                                    {e.imgEven ? (
                                        <img
                                            src={e.imgEven}
                                            alt=""
                                            loading="lazy"
                                            className={`w-full h-full object-cover
                                                ${!e.isActive ? "grayscale opacity-50" : ""}`}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center
                                                        justify-center text-white/30">
                                            <CalendarPlus size={18} />
                                        </div>
                                    )}
                                </div>

                                {/* Infos */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-[#9B59B6] text-xs
                                                         font-bold">
                                            {heureDe(e.dateEven)}
                                        </span>

                                        {!e.isActive && (
                                            <span className="px-1.5 py-0.5 rounded
                                                             bg-gray-600/40
                                                             text-gray-300
                                                             text-[10px] font-semibold">
                                                Désactivé
                                            </span>
                                        )}

                                        {e.estPasse && e.isActive && (
                                            <span className="px-1.5 py-0.5 rounded
                                                             bg-blue-500/20
                                                             text-blue-300
                                                             text-[10px] font-semibold">
                                                Terminé
                                            </span>
                                        )}
                                    </div>

                                    <h3 className="text-black text-sm font-semibold
                                                   leading-tight line-clamp-1">
                                        {e.nomEven}
                                    </h3>

                                    {e.villeEven && (
                                        <p className="flex items-center gap-1
                                                      text-gray-500 text-xs mt-1">
                                            <MapPin size={10} aria-hidden="true" />
                                            <span className="truncate">
                                                {e.villeEven}
                                            </span>
                                        </p>
                                    )}

                                    {/* Statistiques */}
                                    <div className="flex items-center gap-3 mt-1.5
                                                    text-gray-600 text-[11px]">
                                        <span className="flex items-center gap-1">
                                            <Eye size={10} aria-hidden="true" />
                                            {e.nbr_vues}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Heart size={10} aria-hidden="true" />
                                            {e.nbr_jaime}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <MousePointerClick
                                                size={10}
                                                aria-hidden="true"
                                            />
                                            {e.nbr_clicks}
                                        </span>
                                    </div>
                                </div>

                                <Pencil
                                    size={15}
                                    className="text-gray-600 shrink-0 mt-1"
                                    aria-hidden="true"
                                />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {/* ══ AJOUTER ══ */}
            {!estPasse && (
                <div className="p-3 border-t border-white/10">
                    <button
                        type="button"
                        onClick={ajouterCeJour}
                        className="w-full flex items-center justify-center gap-2
                                   py-2.5 rounded-xl bg-[#5F33BA]
                                   hover:brightness-110 transition
                                   text-white text-sm font-semibold
                                   focus:outline-none focus:ring-2
                                   focus:ring-[#9B59B6] focus:ring-offset-2
                                   focus:ring-offset-black"
                    >
                        <CalendarPlus size={16} aria-hidden="true" />
                        Ajouter un événement ce jour
                    </button>
                </div>
            )}
        </div>
    );
};

export default PanneauJourCreateur;